/**
 * MediaPackExpanded - Pack expandido com todas as mídias
 * Módulo Motorista - Sistema IDOOH
 */

import React from 'react';
import { X, Play, DollarSign, Calendar, Image, Video } from 'lucide-react';
import { MediaPack, CampaignMedia } from '../types/dashCompanion';

interface MediaPackExpandedProps {
    pack: MediaPack;
    onClose: () => void;
    onActivate: (pack: MediaPack) => void;
}

export function MediaPackExpanded({ pack, onClose, onActivate }: MediaPackExpandedProps) {
    const formatDate = (date: Date) => {
        return new Date(date).toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit'
        });
    };

    const handleActivate = () => {
        onActivate(pack);
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center">
            {/* Backdrop */}
            <div className="absolute inset-0" onClick={onClose} />

            {/* Modal */}
            <div className="relative w-full max-w-lg bg-white rounded-t-3xl sm:rounded-2xl max-h-[90vh] flex flex-col animate-slide-up">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-gradient-to-r from-emerald-600 to-green-700 rounded-t-3xl sm:rounded-t-2xl">
                    <div>
                        <h2 className="text-lg font-semibold text-white">{pack.name}</h2>
                        <p className="text-sm text-white/70">{pack.mediaCount} mídias disponíveis</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-white/20 rounded-full transition-colors"
                    >
                        <X className="w-6 h-6 text-white" />
                    </button>
                </div>

                {/* Content - Grid de mídias */}
                <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
                    <div className="grid grid-cols-2 gap-3">
                        {pack.medias.map((media) => (
                            <MediaThumbnailCard key={media.id} media={media} />
                        ))}
                    </div>
                </div>

                {/* Footer - Botão Transmitir */}
                <div className="p-4 border-t border-gray-100 bg-white">
                    <div className="flex items-center justify-between mb-3">
                        <span className="text-sm text-gray-600">Ganhos estimados:</span>
                        <span className="text-lg font-bold text-emerald-600 flex items-center gap-1">
                            <DollarSign className="w-5 h-5" />
                            R$ {pack.totalEarnings.toFixed(2)}
                        </span>
                    </div>
                    <button
                        onClick={handleActivate}
                        className="w-full py-4 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white font-semibold rounded-xl shadow-lg flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
                    >
                        <Play className="w-5 h-5" />
                        Transmitir e Monetizar
                    </button>
                </div>
            </div>
        </div>
    );
}

// Componente interno para thumbnail individual
function MediaThumbnailCard({ media }: { media: CampaignMedia }) {
    const formatDate = (date: Date) => {
        return new Date(date).toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit'
        });
    };

    return (
        <div className="bg-white border-2 border-emerald-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md hover:border-emerald-400 transition-all">
            {/* Thumbnail */}
            <div className="relative aspect-video">
                <img
                    src={media.thumbnailUrl}
                    alt={media.title}
                    className="w-full h-full object-cover"
                />
                <div className="absolute top-2 right-2">
                    {media.type === 'video' ? (
                        <div className="bg-emerald-600 rounded-full p-1 shadow-md">
                            <Video className="w-3 h-3 text-white" />
                        </div>
                    ) : (
                        <div className="bg-emerald-600 rounded-full p-1 shadow-md">
                            <Image className="w-3 h-3 text-white" />
                        </div>
                    )}
                </div>
            </div>

            {/* Info */}
            <div className="p-2">
                <p className="text-xs font-semibold text-gray-800 truncate">{media.title}</p>
                <div className="flex items-center gap-1 mt-1 text-xs text-gray-600">
                    <Calendar className="w-3 h-3 text-emerald-600" />
                    <span>{formatDate(media.startDate)} - {formatDate(media.endDate)}</span>
                </div>
                {media.estimatedEarnings && (
                    <div className="flex items-center gap-1 mt-1 text-xs text-emerald-700 font-bold">
                        <DollarSign className="w-3 h-3" />
                        <span>R$ {media.estimatedEarnings.toFixed(2)}</span>
                    </div>
                )}
            </div>
        </div>
    );
}

export default MediaPackExpanded;
